import { useMemo } from "react"
import { parseProfileEvent } from "@/nostr/api/profiles"
import { KINDS, type Profile } from "@/nostr/types"
import { useEventVersion, useNostr } from "@/state/NostrContext"
import { npubOf } from "@/utils/nip19"

function matches(profile: Profile, needle: string): boolean {
  const haystack = [
    profile.name,
    profile.displayName,
    profile.nip05,
    profile.pubkey,
    npubOf(profile.pubkey),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
  return haystack.includes(needle)
}

/**
 * Searches the profiles the client already knows (kind 0 events in the event
 * store) by name, username, NIP-05 address, hex key or npub.
 *
 * No request is made: the author filter reuses the profile lookup the feeds
 * already perform, so the picker always works offline-first and never burns a
 * REQ slot of its own.
 */
export function useProfileSearch(term: string, limit = 8): Profile[] {
  const { store } = useNostr()
  const version = useEventVersion(store)

  return useMemo(() => {
    const needle = term.trim().toLowerCase()
    if (!needle) return []
    const results: Array<{ profile: Profile; score: number }> = []
    for (const event of store.values()) {
      if (event.kind !== KINDS.PROFILE) continue
      const profile = parseProfileEvent(event)
      if (!profile || !matches(profile, needle)) continue
      // Prefix matches on the display name rank first, then anything else.
      const name = (profile.displayName || profile.name || "").toLowerCase()
      const score = name === needle ? 0 : name.startsWith(needle) ? 1 : 2
      results.push({ profile, score })
      if (results.length >= 200) break
    }
    results.sort((a, b) => a.score - b.score || (a.profile.displayName ?? "").localeCompare(b.profile.displayName ?? ""))
    return results.slice(0, limit).map((item) => item.profile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term, limit, version, store])
}
